import React, { FC, memo, useEffect, useState } from "react";
import { AmsaInputProps } from "../../interfaces/AmsaInputs";

const LMP: FC<AmsaInputProps> = ({
  data,
  setValue,
  shouldFocus,
}: AmsaInputProps) => {
  const [weeks, setWeeks] = useState<number>(0);
  const [days, setDays] = useState<number>(0);

  useEffect(() => {
    if (data.value) {
      calculateDifference(data.value);
    } else {
      setWeeks(0);
      setDays(0);
    }
  }, [data.value]);

  const calculateDifference = (lmpDate: string | Date) => {
    try {
      const today = new Date();
      const lmp = new Date(lmpDate);
      
      // Check if date is valid
      if (isNaN(lmp.getTime())) {
        setWeeks(0);
        setDays(0);
        return;
      }
      
      // Reset time to midnight for accurate day calculation
      today.setHours(0, 0, 0, 0);
      lmp.setHours(0, 0, 0, 0);
      
      // Calculate difference in milliseconds
      const diffTime = today.getTime() - lmp.getTime();
      
      // Calculate difference in days
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Only calculate if LMP is in the past
      if (diffDays < 0) {
        setWeeks(0);
        setDays(0);
        return;
      }
      
      // Calculate weeks and remaining days
      const calculatedWeeks = Math.floor(diffDays / 7);
      const calculatedDays = diffDays % 7;
      
      setWeeks(calculatedWeeks);
      setDays(calculatedDays);
    } catch (error) {
      setWeeks(0);
      setDays(0);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e);
    // The calculation will happen in useEffect when data.value updates
  };

  return (
    <div className="block">
      <label className="block mb-2 text-sm font-medium leading-6 text-gray-900">
        {data.label || "LMP"}
      </label>
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <input
            type="date"
            onChange={handleDateChange}
            value={(() => {
              if (!data.value) return "";
              // Handle different date formats
              if (typeof data.value === 'string') {
                // If it's already in YYYY-MM-DD format, use it directly
                if (data.value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                  return data.value;
                }
                // If it's an ISO string, extract the date part
                if (data.value.includes('T')) {
                  return data.value.split('T')[0];
                }
                // Try to parse and format
                const date = new Date(data.value);
                if (!isNaN(date.getTime())) {
                  return date.toISOString().split('T')[0];
                }
              }
              // If it's a Date object
              if (data.value instanceof Date) {
                return data.value.toISOString().split('T')[0];
              }
              return "";
            })()}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
        </div>
        {data.value && (
          <div className="text-sm font-medium text-gray-700 whitespace-nowrap">
            {weeks} {weeks === 1 ? "week" : "weeks"} {days} {days === 1 ? "day" : "days"}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(LMP);
